// The Featured Listings page's MLS ID tables: each Featured row's state and fix, each billable agent's slot on a
// board, and what an agent's Edit opens.
import { AgentMlsIdResource } from "api/resource/agent-mls-id";
import { ClientBoardResource } from "api/resource/client-board";
import { AgentMlsIds, ClientBoardLinkState } from "model/agent-mls-ids";
import { alternateIdOf, entriesOf } from "shared/agent-mls-ids";

const boardOrder = (agentMlsIds: AgentMlsIds, boardId: number) => agentMlsIds.boards.findIndex((board) => board.boardId === boardId);
const entryById = (agentMlsIds: AgentMlsIds, entryId: number) => agentMlsIds.entries.find((entry) => entry.id === entryId) ?? null;
// The entries with a Featured row's value.
const holdersOfRow = (agentMlsIds: AgentMlsIds, rowId: number) => agentMlsIds.entries.filter((entry) => entry.clientBoardIds.includes(rowId));

// Which MLS ID table the account gets. Office-type accounts with agent-sourced Featured Listings get the agents' table, with its
// states and fixes. Office-sourced accounts, and single-agent accounts, get a plain table of client-board rows with
// add, edit and remove.
type MlsIdTableKind = "agents" | "office" | "singleAgent";

export const tableKind = (agentMlsIds: AgentMlsIds): MlsIdTableKind => (agentMlsIds.officeAccount ? "office" : agentMlsIds.agentAccount ? "agents" : "singleAgent");

// ---------- Featured rows ----------
//   aligned      paired with an agent's row
//   shared       several agents hold the value with too few slots
//   duplicate    an orphan of a value that has holders: more slots than rows that need one
//   nonBillable  an orphan whose value only non-billable or opted-out rows hold
//   unknown      an orphan whose value no row holds
type RowState = "aligned" | "duplicate" | "shared" | "unknown" | "nonBillable";
// The Fix icon's named fix; null means the row gets the pencil. Keep one is for a duplicate with one holder, where the
// person picks which slot stays; any other orphan is reviewed on its own.
type RowFix = "keepOne" | "chooseOwner" | "remove" | null;

export interface FeaturedRow {
	clientBoardId: number;
	boardId: number;
	mlsId: string;
	alternateId: string | null;
	state: RowState;
	fix: RowFix;
	// The agents named against the row: its holder when aligned; the holders when shared or a duplicate; the agents
	// with the value when not billable.
	agentIds: number[];
}

const featuredRowOf = (agentMlsIds: AgentMlsIds, row: ClientBoardResource): FeaturedRow => {
	const base = { clientBoardId: row.id, boardId: row.boardId, mlsId: row.agentId, alternateId: alternateIdOf(row) };
	const holderId = agentMlsIds.holderOf.get(row.id);
	if (holderId != null) {
		return { ...base, state: "aligned", fix: null, agentIds: [entryById(agentMlsIds, holderId)!.agentId] };
	}
	const all = holdersOfRow(agentMlsIds, row.id);
	const holders = all.filter((entry) => entry.needsSlot);
	const agentsOf = (list: AgentMlsIdResource[]) => Array.from(new Set(list.map((entry) => entry.agentId))).sort((a, b) => a - b);
	if (agentMlsIds.sharedRows.has(row.id)) {
		return { ...base, state: "shared", fix: "chooseOwner", agentIds: agentsOf(holders) };
	}
	if (all.length === 0) {
		return { ...base, state: "unknown", fix: "remove", agentIds: [] };
	}
	if (holders.length === 0) {
		return { ...base, state: "nonBillable", fix: "remove", agentIds: agentsOf(all) };
	}
	return { ...base, state: "duplicate", fix: holders.length === 1 ? "keepOne" : "remove", agentIds: agentsOf(holders) };
};

// Every Featured row with its state, in board order, then row id.
export const featuredRows = (agentMlsIds: AgentMlsIds): FeaturedRow[] => {
	return Array.from(agentMlsIds.rows.values())
		.sort((a, b) => boardOrder(agentMlsIds, a.boardId) - boardOrder(agentMlsIds, b.boardId) || a.id - b.id)
		.map((row) => featuredRowOf(agentMlsIds, row));
};

// ---------- Featured table for one board ----------
//   on      paired with a slot
//   off     opted out
//   shared  holds a Shared value
//   notSet  no MLS ID on Featured on the board. Strays never show on the Featured page, so an agent with only strays
//           reads as Not set.
type SlotState = "on" | "off" | "shared" | "notSet";

// One per billable agent per board.
export interface FeaturedSlot {
	agentId: number;
	boardId: number;
	state: SlotState;
	// The entry behind the slot: every state but notSet has one.
	entryId: number | null;
	// Its Featured row: on and shared have one.
	clientBoardId: number | null;
	// off: the entry's MLS ID. Otherwise read it from the row.
	mlsId: string | null;
	// off: the Backup Office MLS ID held while off Featured. Otherwise the row's.
	alternateId: string | null;
	// The agent's other paired rows on the board, shown under the slot.
	extraClientBoardIds: number[];
}

interface FeaturedTable {
	boardId: number;
	// Billable agents, in name order.
	slots: FeaturedSlot[];
	// Rows no slot claims: orphans, and Shared rows of agents not shown there.
	unclaimed: number[];
	// Every Featured row on the board, by id.
	rows: Record<number, FeaturedRow>;
}

export const featuredTable = (agentMlsIds: AgentMlsIds, boardId: number): FeaturedTable => {
	const rows: Record<number, FeaturedRow> = {};
	featuredRows(agentMlsIds).filter((row) => row.boardId === boardId).forEach((row) => {
		rows[row.clientBoardId] = row;
	});
	const claimed = new Set<number>();

	const billable = Array.from(agentMlsIds.agents.values())
		.filter((agent) => agent.billable)
		.sort((a, b) => a.name.localeCompare(b.name));
	const slots = billable.map((agent): FeaturedSlot => {
		const entries = entriesOf(agentMlsIds, agent.id, boardId);
		const ofState = (state: ClientBoardLinkState) => entries.filter((entry) => agentMlsIds.clientBoardLinks.get(entry.id)!.state === state);
		const linked = ofState("linked").sort((a, b) => agentMlsIds.slotOf.get(a.id)! - agentMlsIds.slotOf.get(b.id)!);
		const slot = (state: SlotState, entry: AgentMlsIdResource | null, rowId: number | null = null): FeaturedSlot => {
			const row = rowId != null ? rows[rowId] : undefined;
			return {
				agentId: agent.id,
				boardId,
				state,
				entryId: entry ? entry.id : null,
				clientBoardId: row ? row.clientBoardId : null,
				mlsId: row ? row.mlsId : null,
				alternateId: row ? row.alternateId : null,
				extraClientBoardIds: [],
			};
		};

		let result: FeaturedSlot;
		const shared = ofState("shared")[0];
		const off = ofState("off")[0];
		if (linked.length) {
			result = slot("on", linked[0], agentMlsIds.slotOf.get(linked[0].id)!);
			result.extraClientBoardIds = linked.slice(1).map((entry) => agentMlsIds.slotOf.get(entry.id)!);
		} else if (shared) {
			result = slot("shared", shared, agentMlsIds.clientBoardLinks.get(shared.id)!.clientBoardId);
		} else if (off) {
			result = { ...slot("off", off), mlsId: off.mlsId, alternateId: off.alternateId };
		} else {
			result = slot("notSet", null);
		}
		[result.clientBoardId, ...result.extraClientBoardIds].forEach((rowId) => {
			if (rowId != null) {
				claimed.add(rowId);
			}
		});
		return result;
	});

	const owner = (row: FeaturedRow) => {
		const agentId = row.agentIds.find((id) => agentMlsIds.agents.has(id));
		return agentId != null ? agentMlsIds.agents.get(agentId)!.name : null;
	};
	const unclaimed = Object.values(rows)
		.filter((row) => !claimed.has(row.clientBoardId))
		.sort((a, b) => {
			const ownerA = owner(a), ownerB = owner(b);
			return Number(ownerA === null) - Number(ownerB === null)
				|| (ownerA ?? "").localeCompare(ownerB ?? "")
				|| a.mlsId.localeCompare(b.mlsId);
		})
		.map((row) => row.clientBoardId);

	return { boardId, slots, unclaimed, rows };
};

// What Edit on a slot opens: the opted-out ID's edit, the profile, or a new MLS ID. Strays are left to the profile's
// review list, so an agent with only strays gets a new MLS ID.
type SlotAction =
	| { kind: "edit"; entryId: number }
	| { kind: "profile" }
	| { kind: "set" };

export const slotAction = (agentMlsIds: AgentMlsIds, agentId: number, boardId: number): SlotAction => {
	const entries = entriesOf(agentMlsIds, agentId, boardId).filter((entry) => agentMlsIds.clientBoardLinks.get(entry.id)!.state !== "stray");
	const off = entries.find((entry) => agentMlsIds.clientBoardLinks.get(entry.id)!.state === "off" && entry.aligned);
	if (off) {
		return { kind: "edit", entryId: off.id };
	}
	return entries.length > 0 ? { kind: "profile" } : { kind: "set" };
};

// ---------- The plain table: office-sourced and single-agent accounts ----------
// Every live client-board row, in board order, then MLS ID.
export const plainRows = (agentMlsIds: AgentMlsIds): ClientBoardResource[] => {
	return Array.from(agentMlsIds.rows.values())
		.sort((a, b) => boardOrder(agentMlsIds, a.boardId) - boardOrder(agentMlsIds, b.boardId) || a.agentId.localeCompare(b.agentId));
};
